<script setup lang="ts">
import type { ListItem } from '@/components/descLists/StatusDescList.vue';

import StatusDescList from '@/components/descLists/StatusDescList.vue';
import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';

export interface Status {
  color?: 'gray' | 'grey' | 'green' | 'red' | 'orange' | 'violet';
  icon?: string;
  name?: string | null;
  tooltip?: string;
  list?: ListItem[];
}

export interface Props {
  statuses?: Status[];
}
const props = withDefaults(defineProps<Props>(), {
  statuses: () => [],
});

const defStatusColor: Status['color'] = 'gray';
</script>

<template>
  <div
    :class="$style.bigTrafficLightDiagram"
    data-testid="bigTrafficLightDiagram"
  >
    <div :class="$style.statuses">
      <div
        v-if="!props.statuses.length || props.statuses.length <= 2"
        :class="$style.status"
      >
        <div :class="$style.statusTop">
          <div :class="[$style.circle, $style['circle_' + defStatusColor]]">
          </div>
        </div>
      </div>
      <div
        v-for="(status, index) in props.statuses"
        v-else
        :key="index"
        :class="$style.status"
      >
        <div :class="$style.statusTop">
          <div
            :class="[
              $style.circle,
              $style['circle_' + (status.color ?? defStatusColor)],
            ]"
          >
            <v-icon v-if="status.icon" :icon="status.icon"></v-icon>
          </div>
          <div v-if="index === 0" :class="$style.statusTitle">Booked</div>
          <div
            v-else-if="index === statuses.length - 1"
            :class="$style.statusTitle"
          >
            Completed
          </div>
          <div v-else :class="$style.statusTitle">Stop {{ index }}</div>
          <div v-if="index !== statuses.length - 1" :class="$style.statusRoad">
          </div>
        </div>
        <div :class="$style.statusBottom">
          <div :class="$style.statusName">
            <span class="whitespace-nowrap">
              <KParentTooltip v-if="status.tooltip">
                {{ status.tooltip }}
              </KParentTooltip>
              {{ status.name }}
            </span>
          </div>
          <StatusDescList :class="$style.statusDescList" :list="status.list" />
        </div>
      </div>
    </div>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

$circle-size: 25px;
$circle-margin: 8px;

.bigTrafficLightDiagram {
  line-height: 1.4;
}
  .statuses {
    display: flex;
  }
    .status {
      width: 100%;
      position: relative;
    }
    .status:last-child {
      width: auto;
      min-width: 230px;
    }
      .statusTop {
        display: flex;
        align-items: center;
        line-height: 1;
      }
        .circle {
          border-radius: 50%;
          background-color: colors.$color-text-disabled;
          width: $circle-size;
          height: $circle-size;
          flex-shrink: 0;
          margin-right: $circle-margin;
          color: colors.$color-white;
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .circle_green {
          background-color: colors.$color-status-success-icon;
        }
        .circle_red {
          background-color: colors.$color-red-500;
        }
        .circle_orange {
          background-color: colors.$color-orange-400;
        }
        .circle_violet {
          background-color: colors.$color-blue-400;
        }
        .statusTitle {
          font-weight: 500;
          flex-shrink: 0;
        }
        .statusRoad {
          height: 1px;
          background-color: colors.$color-text-tertiary;
          width: 100%;
          margin-left: 4px;
          margin-right: 4px;
        }
      .statusBottom {
        padding-left: $circle-size + $circle-margin;
      }
        .statusName {
          margin: 8px 0;
        }
        .statusDescList {
        }
</style>
